import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuthSession, saveAuthSession, clientApi, bookingApi, clientNoteApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';

const FOCUS_OPTIONS = [
  'Anxiety & Worry',
  'Depression & Mood',
  'Stress & Burnout',
  'Relationship Issues',
  'Career & Life Transitions',
  'Self-Esteem & Confidence',
  'Grief & Loss',
  'Trauma & PTSD',
  'Sleep & Lifestyle',
  'Family & Parenting',
  'Anger Management',
  'Loneliness & Social Connection'
];

export default function ClientProfilePage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  // Active Tab
  const [activeTab, setActiveTab] = useState('personal');

  // Loading & State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Stats
  const [bookingCount, setBookingCount] = useState(0);
  const [worksheetCount, setWorksheetCount] = useState(0);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gender: '',
    dateOfBirth: '',
    preferredLanguage: 'English',
    city: '',
    state: '',
    country: 'India',
    therapyGoals: '',
    focusAreas: [],
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelation: '',
    bio: '',
    notifications: {
      email: true,
      whatsapp: true,
      sms: false,
    },
    createdAt: null,
  });

  // Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Fetch Profile & Stats
  useEffect(() => {
    if (!authSession?.token) {
      navigate('/login');
      return;
    }

    const loadProfileData = async () => {
      try {
        setLoading(true);
        setErrorMessage('');

        const res = await clientApi.getProfile();
        const user = res.user || res || {};

        setFormData({
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          gender: user.gender || '',
          dateOfBirth: user.dateOfBirth ? user.dateOfBirth.substring(0, 10) : '',
          preferredLanguage: user.preferredLanguage || 'English',
          city: user.city || '',
          state: user.state || '',
          country: user.country || 'India',
          therapyGoals: user.therapyGoals || '',
          focusAreas: Array.isArray(user.focusAreas) ? user.focusAreas : [],
          emergencyContactName: user.emergencyContactName || '',
          emergencyContactPhone: user.emergencyContactPhone || '',
          emergencyContactRelation: user.emergencyContactRelation || '',
          bio: user.bio || '',
          notifications: {
            email: user.notifications?.email !== false,
            whatsapp: user.notifications?.whatsapp !== false,
            sms: !!user.notifications?.sms,
          },
          createdAt: user.createdAt || null,
        });

        // Load stats
        try {
          const bookingsRes = await bookingApi.mine();
          const bList = bookingsRes.bookings || bookingsRes.data || bookingsRes || [];
          if (Array.isArray(bList)) setBookingCount(bList.length);
        } catch (e) {
          // stats are non-critical
        }

        try {
          const wsRes = await clientNoteApi.getClientWorksheets();
          const wList = wsRes.worksheets || wsRes || [];
          if (Array.isArray(wList)) setWorksheetCount(wList.length);
        } catch (e) {
          // stats are non-critical
        }
      } catch (err) {
        console.error('Failed to load client profile:', err);
        setErrorMessage(err.message || 'Failed to load profile details.');
      } finally {
        setLoading(false);
      }
    };

    loadProfileData();
  }, [authSession?.token, navigate]);

  // Handle Input Changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (successMessage) setSuccessMessage('');
  };

  const handleNotificationToggle = (key) => {
    setFormData((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: !prev.notifications[key],
      },
    }));
  };

  const toggleFocusArea = (area) => {
    setFormData((prev) => {
      const current = prev.focusAreas || [];
      if (current.includes(area)) {
        return { ...prev, focusAreas: current.filter((a) => a !== area) };
      } else {
        return { ...prev, focusAreas: [...current, area] };
      }
    });
  };

  // Submit Profile Form
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setSaving(true);

    try {
      if (!formData.name.trim()) {
        throw new Error('Full Name is required');
      }

      const res = await clientApi.updateProfile({
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth,
        preferredLanguage: formData.preferredLanguage,
        city: formData.city.trim(),
        state: formData.state.trim(),
        country: formData.country.trim(),
        therapyGoals: formData.therapyGoals.trim(),
        focusAreas: formData.focusAreas,
        emergencyContactName: formData.emergencyContactName.trim(),
        emergencyContactPhone: formData.emergencyContactPhone.trim(),
        emergencyContactRelation: formData.emergencyContactRelation.trim(),
        bio: formData.bio.trim(),
        notifications: formData.notifications,
      });

      // Update auth session in localStorage so navbar & greeting reflect change immediately
      const updatedUser = res.user;
      if (updatedUser && authSession) {
        saveAuthSession({
          ...authSession,
          user: {
            ...authSession.user,
            name: updatedUser.name,
            phone: updatedUser.phone,
            ...updatedUser,
          },
        });
      }

      setSuccessMessage('🎉 Profile details updated successfully!');
      window.scrollTo({ top: 120, behavior: 'smooth' });
    } catch (err) {
      console.error('Profile update failed:', err);
      setErrorMessage(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Submit Password Change
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwordData.currentPassword) {
      setPasswordError('Please enter your current password');
      return;
    }

    if (!passwordData.newPassword) {
      setPasswordError('Please enter a new password');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setPasswordSaving(true);

    try {
      const res = await clientApi.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      setPasswordSuccess(res.message || '🔐 Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err) {
      console.error('Password change failed:', err);
      setPasswordError(err.message || 'Failed to update password. Verify current password.');
    } finally {
      setPasswordSaving(false);
    }
  };

  // Calculate Profile Completion %
  const calculateCompletion = () => {
    let score = 0;
    const fields = [
      formData.name,
      formData.email,
      formData.phone,
      formData.gender,
      formData.dateOfBirth,
      formData.city,
      formData.therapyGoals,
      formData.focusAreas.length > 0,
      formData.emergencyContactName && formData.emergencyContactPhone,
    ];
    fields.forEach((f) => {
      if (f) score += 1;
    });
    return Math.round((score / fields.length) * 100);
  };

  const completionPct = calculateCompletion();

  const getInitials = (name) => {
    if (!name) return 'C';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const memberSince = formData.createdAt
    ? new Date(formData.createdAt).toLocaleDateString('en-IN', {
        month: 'short',
        year: 'numeric',
      })
    : 'Recently';

  return (
    <>
      <DashboardSidebar userRole="client" />

      <div
        className="dashboard-page-content"
        style={{
          marginLeft: '280px',
          minHeight: '100vh',
          background: '#f8fafc',
          padding: '30px 40px',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          color: '#1e293b',
        }}
      >
        {/* Navigation Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', fontSize: '14px', color: '#64748b' }}>
          <Link to="/client-dashboard" style={{ color: '#7c3aed', textDecoration: 'none', fontWeight: 500 }}>
            Dashboard
          </Link>
          <span>/</span>
          <span style={{ color: '#334155', fontWeight: 600 }}>My Profile</span>
        </div>

        {/* Hero Header Card */}
        <div
          style={{
            background: 'linear-gradient(135deg, #6d28d9 0%, #7c3aed 50%, #8b5cf6 100%)',
            borderRadius: '20px',
            padding: '32px',
            color: 'white',
            boxShadow: '0 10px 25px -5px rgba(124, 58, 237, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '24px',
            marginBottom: '30px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Subtle decorative background circles */}
          <div
            style={{
              position: 'absolute',
              right: '-40px',
              top: '-40px',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.08)',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: '120px',
              bottom: '-60px',
              width: '160px',
              height: '160px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.05)',
              pointerEvents: 'none',
            }}
          />

          {/* Left: Avatar & Identity */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '22px', zIndex: 1 }}>
            <div
              style={{
                width: '84px',
                height: '84px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                border: '3px solid rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
                fontWeight: 700,
                color: 'white',
                boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                backdropFilter: 'blur(8px)',
              }}
            >
              {getInitials(formData.name)}
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
                <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 700, letterSpacing: '-0.5px' }}>
                  {formData.name || 'Client Profile'}
                </h1>
                <span
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    padding: '3px 10px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  🌿 Client Member
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '14px', opacity: 0.9, flexWrap: 'wrap' }}>
                <span>✉️ {formData.email || 'No email provided'}</span>
                <span>•</span>
                <span>📅 Member since {memberSince}</span>
              </div>
            </div>
          </div>

          {/* Right: Quick Stats */}
          <div style={{ display: 'flex', gap: '16px', zIndex: 1, flexWrap: 'wrap' }}>
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(12px)',
                borderRadius: '14px',
                padding: '12px 20px',
                textAlign: 'center',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                minWidth: '100px',
              }}
            >
              <div style={{ fontSize: '22px', fontWeight: 800 }}>{bookingCount}</div>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.85 }}>
                Bookings
              </div>
            </div>

            <div
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(12px)',
                borderRadius: '14px',
                padding: '12px 20px',
                textAlign: 'center',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                minWidth: '100px',
              }}
            >
              <div style={{ fontSize: '22px', fontWeight: 800 }}>{worksheetCount}</div>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.85 }}>
                Worksheets
              </div>
            </div>

            <div
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(12px)',
                borderRadius: '14px',
                padding: '12px 20px',
                textAlign: 'center',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                minWidth: '100px',
              }}
            >
              <div style={{ fontSize: '22px', fontWeight: 800 }}>{completionPct}%</div>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.85 }}>
                Completed
              </div>
            </div>
          </div>
        </div>

        {/* Global Feedback Messages */}
        {successMessage && (
          <div
            style={{
              background: '#ecfdf5',
              color: '#065f46',
              border: '1px solid #a7f3d0',
              padding: '16px 20px',
              borderRadius: '12px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.1)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '18px' }}>✓</span>
              <span style={{ fontWeight: 500 }}>{successMessage}</span>
            </div>
            <button
              onClick={() => setSuccessMessage('')}
              style={{ background: 'none', border: 'none', color: '#065f46', cursor: 'pointer', fontSize: '16px' }}
            >
              ✕
            </button>
          </div>
        )}

        {errorMessage && (
          <div
            style={{
              background: '#fef2f2',
              color: '#991b1b',
              border: '1px solid #fecaca',
              padding: '16px 20px',
              borderRadius: '12px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.1)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '18px' }}>⚠️</span>
              <span style={{ fontWeight: 500 }}>{errorMessage}</span>
            </div>
            <button
              onClick={() => setErrorMessage('')}
              style={{ background: 'none', border: 'none', color: '#991b1b', cursor: 'pointer', fontSize: '16px' }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Main Grid: Tabs & Form on Left, Side Summary Card on Right */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: '30px' }}>
          
          {/* Left Column: Tabbed Content */}
          <div>
            {/* Tab Buttons Navigation */}
            <div
              style={{
                display: 'flex',
                gap: '8px',
                background: '#ede9fe',
                padding: '6px',
                borderRadius: '14px',
                marginBottom: '24px',
                overflowX: 'auto',
              }}
            >
              {[
                { id: 'personal', label: 'Personal Info', icon: '👤' },
                { id: 'goals', label: 'Goals & Focus', icon: '🎯' },
                { id: 'emergency', label: 'Emergency Contact', icon: '🛡️' },
                { id: 'security', label: 'Password & Security', icon: '🔒' },
                { id: 'preferences', label: 'Preferences', icon: '🔔' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: 'none',
                    background: activeTab === tab.id ? 'white' : 'transparent',
                    color: activeTab === tab.id ? '#7c3aed' : '#64748b',
                    fontWeight: activeTab === tab.id ? 700 : 500,
                    fontSize: '14px',
                    cursor: 'pointer',
                    boxShadow: activeTab === tab.id ? '0 2px 8px rgba(124, 58, 237, 0.15)' : 'none',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Tab 1: Personal Info */}
            {activeTab === 'personal' && (
              <form onSubmit={handleProfileSubmit}>
                <div
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '30px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                    border: '1px solid #e2e8f0',
                    marginBottom: '24px',
                  }}
                >
                  <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '16px', marginBottom: '24px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                      👤 Personal Details
                    </h3>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                      Manage your profile and communication details
                    </p>
                  </div>

                  {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <div className="spinner" style={{ margin: '0 auto 12px' }}></div>
                      <p style={{ color: '#64748b' }}>Loading profile details...</p>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                            Full Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="e.g. John Doe"
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              borderRadius: '10px',
                              border: '1px solid #cbd5e1',
                              fontSize: '14px',
                              boxSizing: 'border-box',
                              outline: 'none',
                              transition: 'border-color 0.2s',
                            }}
                            onFocus={(e) => (e.target.style.borderColor = '#7c3aed')}
                            onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                          />
                        </div>

                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                            Email Address (Account ID)
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            disabled
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              background: '#f8fafc',
                              color: '#64748b',
                              fontSize: '14px',
                              boxSizing: 'border-box',
                              cursor: 'not-allowed',
                            }}
                          />
                          <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 600, marginTop: '4px', display: 'inline-block' }}>
                            ✓ Verified Email Account
                          </span>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="e.g. +91 98765 43210"
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              borderRadius: '10px',
                              border: '1px solid #cbd5e1',
                              fontSize: '14px',
                              boxSizing: 'border-box',
                              outline: 'none',
                            }}
                            onFocus={(e) => (e.target.style.borderColor = '#7c3aed')}
                            onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                          />
                          <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                            Used for WhatsApp reminders and booking updates
                          </span>
                        </div>

                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                            Gender / Pronouns
                          </label>
                          <select
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              borderRadius: '10px',
                              border: '1px solid #cbd5e1',
                              fontSize: '14px',
                              boxSizing: 'border-box',
                              outline: 'none',
                              background: 'white',
                            }}
                            onFocus={(e) => (e.target.style.borderColor = '#7c3aed')}
                            onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                          >
                            <option value="">Select Gender</option>
                            <option value="male">Male (He/Him)</option>
                            <option value="female">Female (She/Her)</option>
                            <option value="non-binary">Non-Binary (They/Them)</option>
                            <option value="other">Other / Self-Describe</option>
                            <option value="prefer-not-to-say">Prefer not to say</option>
                          </select>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                            Date of Birth
                          </label>
                          <input
                            type="date"
                            name="dateOfBirth"
                            value={formData.dateOfBirth}
                            onChange={handleChange}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              borderRadius: '10px',
                              border: '1px solid #cbd5e1',
                              fontSize: '14px',
                              boxSizing: 'border-box',
                              outline: 'none',
                            }}
                            onFocus={(e) => (e.target.style.borderColor = '#7c3aed')}
                            onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                          />
                        </div>

                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                            Preferred Language
                          </label>
                          <select
                            name="preferredLanguage"
                            value={formData.preferredLanguage}
                            onChange={handleChange}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              borderRadius: '10px',
                              border: '1px solid #cbd5e1',
                              fontSize: '14px',
                              boxSizing: 'border-box',
                              outline: 'none',
                              background: 'white',
                            }}
                            onFocus={(e) => (e.target.style.borderColor = '#7c3aed')}
                            onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                          >
                            <option value="English">English</option>
                            <option value="Hindi">Hindi (हिंदी)</option>
                            <option value="Bengali">Bengali (বাংলা)</option>
                            <option value="Marathi">Marathi (मराठी)</option>
                            <option value="Tamil">Tamil (தமிழ்)</option>
                            <option value="Telugu">Telugu (తెలుగు)</option>
                            <option value="Kannada">Kannada (ಕನ್ನಡ)</option>
                            <option value="Malayalam">Malayalam (മലയാളം)</option>
                            <option value="Gujarati">Gujarati (ગુજરાતી)</option>
                            <option value="Punjabi">Punjabi (ਪੰਜਾਬੀ)</option>
                          </select>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                            City
                          </label>
                          <input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            placeholder="e.g. Mumbai"
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              borderRadius: '10px',
                              border: '1px solid #cbd5e1',
                              fontSize: '14px',
                              boxSizing: 'border-box',
                              outline: 'none',
                            }}
                            onFocus={(e) => (e.target.style.borderColor = '#7c3aed')}
                            onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                          />
                        </div>

                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                            State
                          </label>
                          <input
                            type="text"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            placeholder="e.g. Maharashtra"
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              borderRadius: '10px',
                              border: '1px solid #cbd5e1',
                              fontSize: '14px',
                              boxSizing: 'border-box',
                              outline: 'none',
                            }}
                            onFocus={(e) => (e.target.style.borderColor = '#7c3aed')}
                            onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                          />
                        </div>

                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                            Country
                          </label>
                          <input
                            type="text"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            placeholder="e.g. India"
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              borderRadius: '10px',
                              border: '1px solid #cbd5e1',
                              fontSize: '14px',
                              boxSizing: 'border-box',
                              outline: 'none',
                            }}
                            onFocus={(e) => (e.target.style.borderColor = '#7c3aed')}
                            onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                          />
                        </div>
                      </div>

                      <div style={{ marginTop: '28px', display: 'flex', gap: '12px' }}>
                        <button
                          type="submit"
                          disabled={saving}
                          style={{
                            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            padding: '12px 28px',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: saving ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
                          }}
                        >
                          {saving ? 'Saving...' : '💾 Save Changes'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </form>
            )}

            {/* Tab 2: Therapy Goals & Focus */}
            {activeTab === 'goals' && (
              <form onSubmit={handleProfileSubmit}>
                <div
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '30px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                    border: '1px solid #e2e8f0',
                    marginBottom: '24px',
                  }}
                >
                  <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '16px', marginBottom: '24px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                      🎯 Therapy Focus & Personal Goals
                    </h3>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                      Help your therapists tailor their approach to your unique personal journey
                    </p>
                  </div>

                  <div style={{ marginBottom: '28px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '10px' }}>
                      Primary Areas You’d Like Support With
                    </label>
                    <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '14px' }}>
                      Select the topics that resonate with your current mental wellness priorities.
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {FOCUS_OPTIONS.map((opt) => {
                        const isSelected = formData.focusAreas.includes(opt);
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => toggleFocusArea(opt)}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '20px',
                              border: isSelected ? '1.5px solid #7c3aed' : '1px solid #cbd5e1',
                              background: isSelected ? '#ede9fe' : '#f8fafc',
                              color: isSelected ? '#7c3aed' : '#475569',
                              fontSize: '13px',
                              fontWeight: isSelected ? 600 : 500,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <span>{isSelected ? '✓' : '+'}</span>
                            <span>{opt}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>
                      What are your personal goals or hopes from therapy?
                    </label>
                    <textarea
                      name="therapyGoals"
                      value={formData.therapyGoals}
                      onChange={handleChange}
                      rows={4}
                      placeholder="e.g. I want to build healthier stress coping mechanisms for work, understand my anxiety triggers, and improve communication in my relationships..."
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        borderRadius: '10px',
                        border: '1px solid #cbd5e1',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        outline: 'none',
                        lineHeight: 1.5,
                        resize: 'vertical',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = '#7c3aed')}
                      onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                    />
                    <span style={{ fontSize: '12px', color: '#64748b', marginTop: '6px', display: 'block' }}>
                      🔒 This note is kept private and shared only with certified specialists you book sessions with.
                    </span>
                  </div>

                  <div style={{ marginTop: '28px', display: 'flex', gap: '12px' }}>
                    <button
                      type="submit"
                      disabled={saving}
                      style={{
                        background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '12px 28px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
                      }}
                    >
                      {saving ? 'Saving...' : '💾 Save Goals'}
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* Tab 3: Emergency Contact */}
            {activeTab === 'emergency' && (
              <form onSubmit={handleProfileSubmit}>
                <div
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '30px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                    border: '1px solid #e2e8f0',
                    marginBottom: '24px',
                  }}
                >
                  <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '16px', marginBottom: '24px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                      🛡️ Emergency Contact Information
                    </h3>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                      Required clinical safety standard for online mental health consultations
                    </p>
                  </div>

                  <div
                    style={{
                      background: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      padding: '14px 18px',
                      borderRadius: '10px',
                      color: '#1e40af',
                      fontSize: '13px',
                      lineHeight: 1.5,
                      marginBottom: '24px',
                    }}
                  >
                    ℹ️ <strong>Why is an emergency contact needed?</strong> Clinical ethics guidelines require a designated contact person in the rare event of an acute medical or safety emergency during a consultation. We will never contact this person for billing or non-urgent matters.
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                        Emergency Contact Name
                      </label>
                      <input
                        type="text"
                        name="emergencyContactName"
                        value={formData.emergencyContactName}
                        onChange={handleChange}
                        placeholder="e.g. Priya Sharma"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '10px',
                          border: '1px solid #cbd5e1',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          outline: 'none',
                        }}
                        onFocus={(e) => (e.target.style.borderColor = '#7c3aed')}
                        onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                        Relationship to You
                      </label>
                      <select
                        name="emergencyContactRelation"
                        value={formData.emergencyContactRelation}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '10px',
                          border: '1px solid #cbd5e1',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          outline: 'none',
                          background: 'white',
                        }}
                        onFocus={(e) => (e.target.style.borderColor = '#7c3aed')}
                        onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                      >
                        <option value="">Select Relationship</option>
                        <option value="Spouse / Partner">Spouse / Partner</option>
                        <option value="Parent">Parent</option>
                        <option value="Sibling">Sibling</option>
                        <option value="Close Friend">Close Friend</option>
                        <option value="Guardian">Guardian</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                      Emergency Contact Phone Number
                    </label>
                    <input
                      type="tel"
                      name="emergencyContactPhone"
                      value={formData.emergencyContactPhone}
                      onChange={handleChange}
                      placeholder="e.g. +91 98111 22233"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '10px',
                        border: '1px solid #cbd5e1',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        outline: 'none',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = '#7c3aed')}
                      onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                    />
                  </div>

                  <div style={{ marginTop: '28px', display: 'flex', gap: '12px' }}>
                    <button
                      type="submit"
                      disabled={saving}
                      style={{
                        background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '12px 28px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
                      }}
                    >
                      {saving ? 'Saving...' : '💾 Save Emergency Contact'}
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* Tab 4: Security & Password */}
            {activeTab === 'security' && (
              <form onSubmit={handlePasswordSubmit}>
                <div
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '30px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                    border: '1px solid #e2e8f0',
                    marginBottom: '24px',
                  }}
                >
                  <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '16px', marginBottom: '24px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                      🔒 Password & Security
                    </h3>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                      Update your account password to keep your private wellness records secure
                    </p>
                  </div>

                  {passwordSuccess && (
                    <div
                      style={{
                        background: '#ecfdf5',
                        color: '#065f46',
                        border: '1px solid #a7f3d0',
                        padding: '14px 16px',
                        borderRadius: '10px',
                        marginBottom: '20px',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      {passwordSuccess}
                    </div>
                  )}

                  {passwordError && (
                    <div
                      style={{
                        background: '#fef2f2',
                        color: '#991b1b',
                        border: '1px solid #fecaca',
                        padding: '14px 16px',
                        borderRadius: '10px',
                        marginBottom: '20px',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      {passwordError}
                    </div>
                  )}

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                      Current Password *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                        required
                        placeholder="Enter current password"
                        style={{
                          width: '100%',
                          padding: '12px 42px 12px 16px',
                          borderRadius: '10px',
                          border: '1px solid #cbd5e1',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          outline: 'none',
                        }}
                        onFocus={(e) => (e.target.style.borderColor = '#7c3aed')}
                        onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#64748b',
                          fontSize: '16px',
                        }}
                      >
                        {showCurrentPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                        New Password *
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                          required
                          placeholder="Min. 6 characters"
                          style={{
                            width: '100%',
                            padding: '12px 42px 12px 16px',
                            borderRadius: '10px',
                            border: '1px solid #cbd5e1',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                            outline: 'none',
                          }}
                          onFocus={(e) => (e.target.style.borderColor = '#7c3aed')}
                          onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#64748b',
                            fontSize: '16px',
                          }}
                        >
                          {showNewPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                        Confirm New Password *
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                          required
                          placeholder="Repeat new password"
                          style={{
                            width: '100%',
                            padding: '12px 42px 12px 16px',
                            borderRadius: '10px',
                            border: '1px solid #cbd5e1',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                            outline: 'none',
                          }}
                          onFocus={(e) => (e.target.style.borderColor = '#7c3aed')}
                          onBlur={(e) => (e.target.style.borderColor = '#cbd5e1')}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#64748b',
                            fontSize: '16px',
                          }}
                        >
                          {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '28px', display: 'flex', gap: '12px' }}>
                    <button
                      type="submit"
                      disabled={passwordSaving}
                      style={{
                        background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '12px 28px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: passwordSaving ? 'not-allowed' : 'pointer',
                        boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
                      }}
                    >
                      {passwordSaving ? 'Updating...' : '🔑 Update Password'}
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* Tab 5: Preferences */}
            {activeTab === 'preferences' && (
              <form onSubmit={handleProfileSubmit}>
                <div
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '30px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                    border: '1px solid #e2e8f0',
                    marginBottom: '24px',
                  }}
                >
                  <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '16px', marginBottom: '24px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                      🔔 Notification Preferences
                    </h3>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                      Control how and when you receive session reminders and wellness updates
                    </p>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                    {/* WhatsApp */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px',
                        borderRadius: '12px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div style={{ fontSize: '24px' }}>📱</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>
                            WhatsApp Session Reminders
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            Receive automated meeting links and 1-hour appointment reminders on WhatsApp
                          </div>
                        </div>
                      </div>
                      <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px' }}>
                        <input
                          type="checkbox"
                          checked={formData.notifications.whatsapp}
                          onChange={() => handleNotificationToggle('whatsapp')}
                          style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span
                          style={{
                            position: 'absolute',
                            cursor: 'pointer',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: formData.notifications.whatsapp ? '#10b981' : '#cbd5e1',
                            borderRadius: '34px',
                            transition: '0.3s',
                          }}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              content: "''",
                              height: '20px',
                              width: '20px',
                              left: formData.notifications.whatsapp ? '24px' : '3px',
                              bottom: '3px',
                              backgroundColor: 'white',
                              borderRadius: '50%',
                              transition: '0.3s',
                            }}
                          />
                        </span>
                      </label>
                    </div>

                    {/* Email */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px',
                        borderRadius: '12px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div style={{ fontSize: '24px' }}>✉️</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>
                            Email Notifications & Receipts
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            Receive booking receipts, invoices, and clinical worksheet assignments
                          </div>
                        </div>
                      </div>
                      <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px' }}>
                        <input
                          type="checkbox"
                          checked={formData.notifications.email}
                          onChange={() => handleNotificationToggle('email')}
                          style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span
                          style={{
                            position: 'absolute',
                            cursor: 'pointer',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: formData.notifications.email ? '#7c3aed' : '#cbd5e1',
                            borderRadius: '34px',
                            transition: '0.3s',
                          }}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              content: "''",
                              height: '20px',
                              width: '20px',
                              left: formData.notifications.email ? '24px' : '3px',
                              bottom: '3px',
                              backgroundColor: 'white',
                              borderRadius: '50%',
                              transition: '0.3s',
                            }}
                          />
                        </span>
                      </label>
                    </div>

                    {/* SMS */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px',
                        borderRadius: '12px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div style={{ fontSize: '24px' }}>💬</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>
                            SMS Text Updates
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            Critical schedule changes and OTP verification alerts
                          </div>
                        </div>
                      </div>
                      <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px' }}>
                        <input
                          type="checkbox"
                          checked={formData.notifications.sms}
                          onChange={() => handleNotificationToggle('sms')}
                          style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span
                          style={{
                            position: 'absolute',
                            cursor: 'pointer',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: formData.notifications.sms ? '#7c3aed' : '#cbd5e1',
                            borderRadius: '34px',
                            transition: '0.3s',
                          }}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              content: "''",
                              height: '20px',
                              width: '20px',
                              left: formData.notifications.sms ? '24px' : '3px',
                              bottom: '3px',
                              backgroundColor: 'white',
                              borderRadius: '50%',
                              transition: '0.3s',
                            }}
                          />
                        </span>
                      </label>
                    </div>
                  </div>

                  <div style={{ marginTop: '28px', display: 'flex', gap: '12px' }}>
                    <button
                      type="submit"
                      disabled={saving}
                      style={{
                        background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '12px 28px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
                      }}
                    >
                      {saving ? 'Saving...' : '💾 Save Preferences'}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Right Column: Sticky Summary & Helpful Links Widget */}
          <div>
            {/* Profile Completion Card */}
            <div
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                border: '1px solid #e2e8f0',
                marginBottom: '20px',
              }}
            >
              <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>
                📊 Profile Strength
              </h4>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Completion</span>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#7c3aed' }}>{completionPct}%</span>
              </div>

              <div style={{ height: '8px', background: '#ede9fe', borderRadius: '4px', overflow: 'hidden', marginBottom: '16px' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${completionPct}%`,
                    background: 'linear-gradient(90deg, #7c3aed 0%, #a78bfa 100%)',
                    borderRadius: '4px',
                    transition: 'width 0.5s ease',
                  }}
                />
              </div>

              <ul style={{ paddingLeft: '0', listStyle: 'none', margin: '0 0 16px 0', fontSize: '12px', color: '#475569', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: formData.phone ? '#10b981' : '#94a3b8' }}>{formData.phone ? '✓' : '○'}</span>
                  <span>Phone Number added</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: formData.focusAreas.length > 0 ? '#10b981' : '#94a3b8' }}>{formData.focusAreas.length > 0 ? '✓' : '○'}</span>
                  <span>Focus areas selected</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: formData.therapyGoals ? '#10b981' : '#94a3b8' }}>{formData.therapyGoals ? '✓' : '○'}</span>
                  <span>Therapy goals shared</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: formData.emergencyContactName ? '#10b981' : '#94a3b8' }}>{formData.emergencyContactName ? '✓' : '○'}</span>
                  <span>Emergency contact added</span>
                </li>
              </ul>
            </div>

            {/* Quick Links Card */}
            <div
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                border: '1px solid #e2e8f0',
                marginBottom: '20px',
              }}
            >
              <h4 style={{ margin: '0 0 14px 0', fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>
                ⚡ Quick Actions
              </h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Link
                  to="/therapists"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    background: '#f8fafc',
                    color: '#334155',
                    textDecoration: 'none',
                    fontSize: '13px',
                    fontWeight: 600,
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#ede9fe')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '#f8fafc')}
                >
                  <span>🔍</span>
                  <span>Browse Specialists</span>
                </Link>

                <Link
                  to="/client/bookings"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    background: '#f8fafc',
                    color: '#334155',
                    textDecoration: 'none',
                    fontSize: '13px',
                    fontWeight: 600,
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#ede9fe')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '#f8fafc')}
                >
                  <span>📅</span>
                  <span>My Appointments</span>
                </Link>

                <Link
                  to="/client/worksheets"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    background: '#f8fafc',
                    color: '#334155',
                    textDecoration: 'none',
                    fontSize: '13px',
                    fontWeight: 600,
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#ede9fe')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '#f8fafc')}
                >
                  <span>📑</span>
                  <span>My Clinical Worksheets</span>
                </Link>

                <Link
                  to="/self-help"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    background: '#f8fafc',
                    color: '#334155',
                    textDecoration: 'none',
                    fontSize: '13px',
                    fontWeight: 600,
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#ede9fe')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '#f8fafc')}
                >
                  <span>🧘</span>
                  <span>Self-Help & Audio Tools</span>
                </Link>
              </div>
            </div>

            {/* Privacy Badge */}
            <div
              style={{
                background: 'linear-gradient(135deg, #ede9fe 0%, #fae8ff 100%)',
                borderRadius: '16px',
                padding: '18px',
                border: '1px solid #d8b4fe',
                fontSize: '12px',
                color: '#581c87',
                lineHeight: 1.5,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>🔒</span> 100% Confidential Care
              </div>
              Your data is encrypted following healthcare compliance standards. We never sell your personal information.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
